import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banner, Button, ConfirmDialog, Spinner } from "@/components/ui";
import {
  endBlockPair,
  errorDetail,
  listBlockEvents,
  listUsers,
  reassignBlock,
  releaseBlock,
  reopenBlock,
} from "@/lib/api";
import type { AuthUser, BlockEvent, SurveyBlock } from "@/lib/types";
import { formatDateTime } from "@/lib/format";
import { useLanguage } from "@/i18n";

/** A datetime-local value (local time, no zone) for `hours` from now. */
function localInput(hoursFromNow: number): string {
  const d = new Date(Date.now() + hoursFromNow * 3600_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The ISO time a datetime-local value means, or null if it is not one. */
export function inputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const defaultValidUntil = () => localInput(48);

/**
 * Who holds the selected block and since when, and the office's claim and
 * reopen actions: Release, Reassign, Send back (reason + validity required),
 * and the block's history. Survey data already synced is never touched by any
 * of these -- it belongs to the block, not to whoever holds it.
 */
export default function BlockControls({
  block,
  isOfficeUser,
  onChanged,
}: {
  block: SurveyBlock;
  isOfficeUser: boolean;
  onChanged: () => void;
}) {
  const { t, lang } = useLanguage();
  const c = t.blockControls;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<"release" | "reassign" | "reopen" | "history" | "unpair" | null>(null);
  const [surveyors, setSurveyors] = useState<AuthUser[] | null>(null);
  const [target, setTarget] = useState<string>("");
  const [reason, setReason] = useState("");
  const [validUntil, setValidUntil] = useState(defaultValidUntil());
  const [events, setEvents] = useState<BlockEvent[] | null>(null);

  const when = (iso: string | null | undefined) =>
    iso ? formatDateTime(iso, lang, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  useEffect(() => {
    setError(null);
    setDialog(null);
  }, [block.id]);

  useEffect(() => {
    if ((dialog === "reassign" || dialog === "reopen") && surveyors === null) {
      listUsers()
        .then((all) => setSurveyors(all.filter((u) => u.role === "surveyor")))
        .catch((e) => setError(errorDetail(e)));
    }
    if (dialog === "history") {
      setEvents(null);
      listBlockEvents(block.id)
        .then(setEvents)
        .catch((e) => setError(errorDetail(e)));
    }
  }, [dialog, block.id, surveyors]);

  function open(which: "release" | "reassign" | "reopen" | "history" | "unpair") {
    setError(null);
    setTarget("");
    setReason("");
    setValidUntil(defaultValidUntil());
    setDialog(which);
  }

  async function act(action: () => Promise<unknown>, notice: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      toast.success(notice);
      setDialog(null);
      onChanged();
    } catch (e) {
      setError(errorDetail(e));
    } finally {
      setBusy(false);
    }
  }

  const holder = block.assigned_surveyor;
  const submitted = block.status === "submitted";
  const validIso = inputToIso(validUntil);
  const reasonOk = reason.trim().length >= 10;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md bg-surface px-4 py-2 text-xs shadow-ring">
        <span className="text-ink-secondary">
          {holder ? (
            <>
              {c.claimedBy(holder, when(block.claimed_at))}
              {block.last_activity_at && !submitted && (
                <span className="text-ink-muted"> · {c.lastActivity(when(block.last_activity_at))}</span>
              )}
              {block.claim_expires_at && !submitted && (
                <span className="text-ink-muted"> · {c.expires(when(block.claim_expires_at))}</span>
              )}
            </>
          ) : (
            <span className="text-ink-muted">{c.notClaimed}</span>
          )}
        </span>

        {submitted && (
          <span className="text-ink-muted">
            {isOfficeUser ? t.hierarchyPage.submittedCanEdit : t.hierarchyPage.surveyLocked}
          </span>
        )}

        {isOfficeUser && (
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {block.pair_id != null && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => open("unpair")}>
                {c.endPair}
              </Button>
            )}
            {!submitted && holder && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => open("release")}>
                {c.release}
              </Button>
            )}
            {!submitted && (
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => open("reassign")}>
                {holder ? c.reassign : c.assign}
              </Button>
            )}
            {submitted && (
              <Button size="sm" disabled={busy} onClick={() => open("reopen")} title={t.hierarchyPage.sendBackTitle}>
                {t.hierarchyPage.sendBackToSurveyor}
              </Button>
            )}
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => open("history")}>
              {c.history}
            </Button>
          </div>
        )}
      </div>

      {block.paired_with_name && (
        <Banner tone="info">
          {c.pairedWith(block.paired_with_name)}{" "}
          <span className="text-ink-secondary">{c.pairedHint}</span>
        </Banner>
      )}

      {block.reopen && (
        <Banner tone="warning">
          {c.reopenedUntil(when(block.reopen.valid_until), block.reopen.approved_by ?? "—")}{" "}
          <span className="text-ink-secondary">{c.reasonLabel}: {block.reopen.reason}</span>
        </Banner>
      )}

      {error && dialog === null && <Banner tone="danger">{error}</Banner>}

      {/* Release */}
      <ConfirmDialog
        open={dialog === "release"}
        title={c.releaseTitle}
        body={c.releaseBody(block.name, holder ?? "")}
        confirmLabel={c.release}
        cancelLabel={t.common.cancel}
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => void act(() => releaseBlock(block.id), c.noticeReleased(block.name))}
      >
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>

      {/* End pairing */}
      <ConfirmDialog
        open={dialog === "unpair"}
        danger={false}
        title={c.endPairTitle}
        body={c.endPairBody(block.name, block.paired_with_name ?? "—")}
        confirmLabel={c.endPair}
        cancelLabel={t.common.cancel}
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          if (block.pair_id != null)
            void act(() => endBlockPair(block.pair_id!), c.noticePairEnded(block.name, block.paired_with_name ?? "—"));
        }}
      >
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>

      {/* Reassign */}
      <ConfirmDialog
        open={dialog === "reassign"}
        danger={false}
        title={c.reassignTitle}
        body={c.reassignBody(block.name)}
        confirmLabel={holder ? c.reassign : c.assign}
        cancelLabel={t.common.cancel}
        busy={busy}
        confirmDisabled={!target}
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          const user = surveyors?.find((u) => String(u.id) === target);
          if (user) void act(() => reassignBlock(block.id, user.id), c.noticeReassigned(block.name, user.username));
        }}
      >
        <SurveyorSelect surveyors={surveyors} value={target} onChange={setTarget} exclude={holder} label={c.surveyor} />
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>

      {/* Send back (reopen) */}
      <ConfirmDialog
        open={dialog === "reopen"}
        danger={false}
        title={c.reopenTitle}
        body={c.reopenBody}
        confirmLabel={t.hierarchyPage.sendBackToSurveyor}
        cancelLabel={t.common.cancel}
        busy={busy}
        confirmDisabled={!reasonOk || !validIso}
        onCancel={() => setDialog(null)}
        onConfirm={() => {
          if (!validIso) return;
          const user = target ? Number(target) : undefined;
          void act(
            () => reopenBlock(block.id, { reason: reason.trim(), valid_until: validIso, user_id: user }),
            c.noticeReopened(block.name, when(validIso))
          );
        }}
      >
        <ReopenFields
          reason={reason}
          onReason={setReason}
          validUntil={validUntil}
          onValidUntil={setValidUntil}
          showReason
        />
        <SurveyorSelect
          surveyors={surveyors}
          value={target}
          onChange={setTarget}
          label={c.reopenFor}
          placeholder={holder ? c.reopenForHolder(holder) : c.reopenForNobody}
        />
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>

      {/* History */}
      <ConfirmDialog
        open={dialog === "history"}
        danger={false}
        title={c.historyTitle(block.name)}
        body=""
        confirmLabel={t.common.done}
        cancelLabel={t.common.done}
        onCancel={() => setDialog(null)}
        onConfirm={() => setDialog(null)}
      >
        {events === null ? (
          <Spinner className="h-4 w-4" />
        ) : events.length === 0 ? (
          <p className="text-xs text-ink-muted">{c.historyNone}</p>
        ) : (
          <ul className="thin-scroll max-h-80 space-y-1.5 overflow-y-auto text-xs">
            {events.map((e) => (
              <li key={e.id} className="rounded bg-surface-sunken px-2.5 py-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-medium text-ink">{c.eventLabel(e.event)}</span>
                  <span className="whitespace-nowrap tabular-nums text-ink-muted">{when(e.at)}</span>
                </div>
                <p className="text-2xs text-ink-secondary">{describeEvent(e, c.by, when)}</p>
              </li>
            ))}
          </ul>
        )}
        {error && <Banner tone="danger">{error}</Banner>}
      </ConfirmDialog>
    </div>
  );
}

function describeEvent(
  e: BlockEvent,
  by: (name: string) => string,
  when: (iso: string | null | undefined) => string
): string {
  const d = e.details ?? {};
  const parts: string[] = [];
  if (e.actor) parts.push(by(e.actor));
  if (typeof d.holder === "string") parts.push(`→ ${d.holder}`);
  if (typeof d.previous_holder === "string") parts.push(`(${d.previous_holder} →)`);
  if (typeof d.reopened_for === "string") parts.push(`→ ${d.reopened_for}`);
  if (typeof d.with_block === "string") parts.push(`+ ${d.with_block}`);
  if (typeof d.valid_until === "string") parts.push(`⏱ ${when(d.valid_until)}`);
  if (typeof d.reason === "string") parts.push(`“${d.reason}”`);
  if (typeof d.note === "string" && d.note) parts.push(`“${d.note}”`);
  if (typeof d.why === "string") parts.push(d.why);
  return parts.join(" ");
}

export function ReopenFields({
  reason,
  onReason,
  validUntil,
  onValidUntil,
  showReason,
}: {
  reason: string;
  onReason: (v: string) => void;
  validUntil: string;
  onValidUntil: (v: string) => void;
  showReason: boolean;
}) {
  const { t } = useLanguage();
  const c = t.blockControls;
  return (
    <div className="space-y-2.5">
      {showReason && (
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-ink">{c.reasonLabel} *</span>
          <textarea
            className="w-full rounded border border-line bg-surface px-2.5 py-1.5 text-xs text-ink"
            rows={3}
            value={reason}
            maxLength={2000}
            placeholder={c.reasonPlaceholder}
            onChange={(e) => onReason(e.target.value)}
          />
          <span className="text-2xs text-ink-muted">{c.reasonHint}</span>
        </label>
      )}
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-ink">{c.validUntilLabel} *</span>
        <input
          type="datetime-local"
          className="rounded border border-line bg-surface px-2.5 py-1.5 text-xs text-ink"
          value={validUntil}
          onChange={(e) => onValidUntil(e.target.value)}
        />
        <span className="block text-2xs text-ink-muted">{c.validUntilHint}</span>
      </label>
    </div>
  );
}

function SurveyorSelect({
  surveyors,
  value,
  onChange,
  exclude,
  label,
  placeholder,
}: {
  surveyors: AuthUser[] | null;
  value: string;
  onChange: (v: string) => void;
  exclude?: string | null;
  label: string;
  placeholder?: string;
}) {
  const { t } = useLanguage();
  if (surveyors === null) return <Spinner className="h-4 w-4" />;
  return (
    <label className="mt-2.5 block">
      <span className="mb-1 block text-xs font-medium text-ink">{label}</span>
      <select
        className="w-full rounded border border-line bg-surface px-2.5 py-1.5 text-xs text-ink"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder ?? t.blockControls.chooseSurveyor}</option>
        {surveyors
          .filter((u) => u.username !== exclude)
          .map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.full_name ? `${u.full_name} (${u.username})` : u.username}
            </option>
          ))}
      </select>
    </label>
  );
}
